import {get as _get} from 'lodash';
import React from 'react';
import PropTypes from 'prop-types';
import {Header} from 'semantic-ui-react';
import moment from 'moment';

import {getLink} from '@appComponents/Link';
import {DELETE_CONFIRMATION, NOT_DELETED} from '@constants/messages';
import {IconWithTooltip} from '@appComponents/IconCollection';
import {
    DEBUG_PLATFORM_LINK,
    SECURITY_PRIVILEGES_CREATE_UPDATE_DELETE,
    SECURITY_PRIVILEGES_DELETE,
    SECURITY_PRIVILEGES_READ,
    SECURITY_PRIVILEGES_CREATE,
} from '@constants/variables';
import TriggerAutobookerButton from '@modules/autobooker/components/TriggerAutobookerButton';
import EventContentAlertSteps from '@modules/eventContentAlert/views/EventContentAlertSteps';
import {convertToInt} from '@utils/helpers';
import {showMessageBox} from '@utils/messageBox';
import {
    BOOKING,
    EVENT_BOOKING,
    EVENT_CONTENT,
    EVENT_CONTENT_ALERT,
    OFFERING,
    STREAM,
} from '@constants/resources';
import {Authorization} from '@appComponents/Authorization';
import {EventContentVariantPopup} from '@utils/eventContentVariant/EventContentVariantPopup';
import {hasProperOriginStreamType} from '@modules/liveControl/utils/helpers';
import {hideModal, setErrorModal, showModal, showModalConfirmation} from '@utils/modal';

import {CANT_DELETE_CONTENT} from '../constants/messages';
import {isBeingEncodedQueryHandler, renderModalErrorObject} from '../utils/eventContentHelper';
import {
    HideInLiveControlRenderer,
    Is247StreamRenderer,
    IsWscRenderer,
    MainContentRenderer,
    getTitle,
    TitleRenderer,
    IconOutputStreamModal,
    disableAccordionToggle,
    ClockOrDurationRenderer,
} from '../utils/eventsContentTitle';

const authorizationCreateAlertObject = {
    [EVENT_CONTENT_ALERT]: SECURITY_PRIVILEGES_CREATE,
};

const authorizationDeleteEventContentObject = {
    [EVENT_CONTENT]: SECURITY_PRIVILEGES_DELETE,
};

const authorizationTriggerAutobookerObject = {
    [BOOKING]: SECURITY_PRIVILEGES_CREATE_UPDATE_DELETE,
    [OFFERING]: SECURITY_PRIVILEGES_CREATE_UPDATE_DELETE,
    [EVENT_BOOKING]: SECURITY_PRIVILEGES_CREATE_UPDATE_DELETE,
};

const authorizationStreamReadObject = {
    [STREAM]: SECURITY_PRIVILEGES_READ,
};

export default class EventsContentTitle extends React.Component {
    static propTypes = {
        data: PropTypes.object.isRequired,
        deleteEventContent: PropTypes.func.isRequired,
        eventId: PropTypes.number.isRequired,
        history: PropTypes.object.isRequired,
        hasStream: PropTypes.bool.isRequired,
        loading: PropTypes.bool.isRequired,
    };

    removeHandler = (event) => {
        disableAccordionToggle(event);

        const variables = {
            id: this.props.data.id,
            is_event: false,
        };

        isBeingEncodedQueryHandler(variables)
            .then((result) => {
                if (result.data.isBeingEncoded) {
                    setErrorModal(renderModalErrorObject(CANT_DELETE_CONTENT));
                } else {
                    showModalConfirmation({
                        header: <Header icon='trash' content='Delete event content'/>,
                        text: DELETE_CONFIRMATION(getTitle(variables.id, this.props.data.event_content_type.name, this.props.data.name)),
                        onYes: () => {
                            this.props.deleteEventContent(convertToInt(variables.id));
                            hideModal();
                        },
                    });
                }
            })
            .catch((error) => {
                showMessageBox(
                    'eventLayout',
                    NOT_DELETED('Content'),
                    `${error}`,
                    'error'
                );
            });
    };

    editHandler = (event) => {
        disableAccordionToggle(event);

        const {
            data: {
                id,
            },
            eventId,
        } = this.props;

        this.props.history.replace(
            getLink(
                'events.configuration.content.edit',
                {
                    id: eventId,
                    contentId: id,
                }
            )
        );
    };

    openDebugPlatformInterface = (event) => {
        disableAccordionToggle(event);

        const url = `${DEBUG_PLATFORM_LINK}/#/events/event/${this.props.data.id}`;
        window.open(url, '_blank');
    };

    openModalToSendAlertToClients = (event) => {
        disableAccordionToggle(event);

        showModal({
            isVisible: true,
            size: 'large',
            content:
                <EventContentAlertSteps
                    eventContentStartDatetime={this.props.data.start_datetime}
                    eventContentId={convertToInt(this.props.data.id)}
                    messageBoxName='targetsInfo'
                />,
        });
    };

    render() {
        const {data} = this.props;
        const additionalIconsVisibility = data.has_input &&
            _get(data, 'event_content_input.encoding_first_start_datetime', null);
        const popupProps = {position: 'top right'};
        const isContentInPast = 1 < moment().diff(moment(data.start_datetime), 'h');

        return (
            <div className='eventContent__header --align-flex-end'>
                <TitleRenderer id={data.id} contentTypeName={data.event_content_type.name} name={data.name}/>
                <EventContentVariantPopup eventContentVariant={data.event_content_variant}/>
                <ClockOrDurationRenderer data={data}/>
                <Is247StreamRenderer is247stream={data.is_247_stream}/>
                <MainContentRenderer isMainContent={data.is_main_content}/>
                <HideInLiveControlRenderer isHiddenInLiveControl={data.hide_in_live_control}/>
                <IsWscRenderer id={data.wsc_video_id} md5={data.wsc_video_md5}/>
                <div className='eventContent__headerButtons'>
                    <IconWithTooltip
                        name='setting'
                        onClick={this.editHandler}
                        tooltip='Configure the content'
                        popupProps={popupProps}
                        resource={EVENT_CONTENT}
                    />
                    {
                        additionalIconsVisibility &&
                        <IconWithTooltip
                            name='bug'
                            onClick={this.openDebugPlatformInterface}
                            tooltip='Debug platform'
                            popupProps={popupProps}
                        />
                    }
                    <Authorization authorization={authorizationCreateAlertObject}>
                        <IconWithTooltip
                            name='warning circle'
                            onClick={this.openModalToSendAlertToClients}
                            tooltip='Send alert to clients'
                            popupProps={popupProps}
                        />
                    </Authorization>
                    <Authorization authorization={authorizationStreamReadObject}>
                        {this.props.loading ? (
                            <i aria-hidden='true' className='spinner loading icon'/>
                        ) : ((this.props.hasStream || hasProperOriginStreamType(data.event_content_input?.origin_stream_type.id)) &&
                            <IconOutputStreamModal
                                popupProps={popupProps}
                                eventId={this.props.eventId}
                                eventContentId={this.props.data.id}
                            />
                        )}
                    </Authorization>
                    <Authorization authorization={authorizationTriggerAutobookerObject}>
                        <button type='button' onClick={disableAccordionToggle} className='unstyled-button'>
                            <TriggerAutobookerButton
                                id={data.id}
                                entity='content'
                                messageBoxName='event'
                                popupProps={popupProps}
                                uiType='icon'
                                disabled={isContentInPast}
                            />
                        </button>
                    </Authorization>
                    <Authorization authorization={authorizationDeleteEventContentObject}>
                        <IconWithTooltip
                            name='trash'
                            onClick={this.removeHandler}
                            tooltip='Delete the content'
                            popupProps={popupProps}
                        />
                    </Authorization>
                </div>
            </div>
        );
    }
}
